import { useState, useEffect } from 'react';
import { FileEdit, Plus, Trash2, Loader2, Calendar, FileType, ArrowLeft, Download, Lock } from 'lucide-react';
import { draftApi } from '@/services/api';
import { usePremium } from '@/hooks/usePremium';
import { useAuth } from '@/context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { LineNumberedTextarea } from './LineNumberedTextarea';
import { exportDraftToDocx } from '@/utils/draftExport';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { useNotificationStore } from '@/stores';

interface Draft {
  draft_id: string;
  title: string;
  content: string;
  draft_type: string;
  created_at: string;
  updated_at: string;
  metadata?: any;
}

interface DraftsPageProps {
  onBack: () => void;
  onSelectDraft?: (draft: Draft) => void;
}

export function DraftsPage({ onBack, onSelectDraft }: DraftsPageProps) {
  const { isPremium } = usePremium();
  const { loading: authLoading } = useAuth();
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewDraftDialog, setShowNewDraftDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState<Draft | null>(null);
  const [selectedDraft, setSelectedDraft] = useState<Draft | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // New draft form
  const [newDraftTitle, setNewDraftTitle] = useState('');
  const [newDraftType, setNewDraftType] = useState('general');

  // Draft editor state
  const [draftTitle, setDraftTitle] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [draftType, setDraftType] = useState('general');
  const [isSaving, setIsSaving] = useState(false);

  const { addNotification } = useNotificationStore();

  useEffect(() => {
    loadDrafts();
  }, []);

  const loadDrafts = async () => {
    try {
      setIsLoading(true);
      const response = await draftApi.listDrafts();
      console.log('Loaded drafts:', response.drafts);

      // Normalize the drafts to ensure title and content are strings
      const normalizedDrafts = (response.drafts || []).map((draft: any) => ({
        ...draft,
        title: typeof draft.title === 'string' ? draft.title : (draft.title?.title || 'Untitled Draft'),
        content: typeof draft.content === 'string' ? draft.content : (draft.content?.content || '')
      }));

      setDrafts(normalizedDrafts);
    } catch (error) {
      console.error('Failed to load drafts:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load drafts',
        duration: 3000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateDraft = async () => {
    if (!newDraftTitle.trim()) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Please enter a draft title',
        duration: 3000
      });
      return;
    }

    try {
      const response = await draftApi.createDraft(newDraftTitle, '', newDraftType);
      setShowNewDraftDialog(false);
      setNewDraftTitle('');
      setNewDraftType('general');

      // Select the new draft for editing
      const newDraft = {
        ...response,
        content: ''
      };
      setSelectedDraft(newDraft);
      setDraftTitle(response.title);
      setDraftContent('');

      loadDrafts();

      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Draft created successfully',
        duration: 2000
      });
    } catch (error) {
      console.error('Failed to create draft:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to create draft',
        duration: 3000
      });
    }
  };

  const handleDeleteDraft = async () => {
    if (!draftToDelete) return;

    try {
      setIsDeleting(true);
      await draftApi.deleteDraft(draftToDelete.draft_id);

      setShowDeleteDialog(false);
      setDraftToDelete(null);

      // If deleted draft was selected, close editor
      if (selectedDraft?.draft_id === draftToDelete.draft_id) {
        setSelectedDraft(null);
      }

      // Remove from active drafts in localStorage (so it's removed from Chat Page)
      try {
        const activeDraftsStr = localStorage.getItem('active_drafts');
        if (activeDraftsStr) {
          const activeDrafts = JSON.parse(activeDraftsStr);
          const updatedDrafts = activeDrafts.filter((d: any) => d.draft_id !== draftToDelete.draft_id);
          localStorage.setItem('active_drafts', JSON.stringify(updatedDrafts));
          // Dispatch event so Chat Page updates
          window.dispatchEvent(new Event('storage-update'));
        }
      } catch (error) {
        console.error('Failed to update active drafts:', error);
      }

      loadDrafts();

      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Draft deleted successfully',
        duration: 2000
      });
    } catch (error) {
      console.error('Failed to delete draft:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to delete draft',
        duration: 3000
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSelectDraft = async (draft: Draft) => {
    try {
      const fullDraft = await draftApi.getDraft(draft.draft_id);
      console.log('Selected draft:', fullDraft);

      // Normalize the draft data
      const normalizedDraft = {
        ...fullDraft,
        title: typeof fullDraft.title === 'string' ? fullDraft.title : (fullDraft.title?.title || 'Untitled Draft'),
        content: typeof fullDraft.content === 'string' ? fullDraft.content : (fullDraft.content?.content || '')
      };

      setSelectedDraft(normalizedDraft);
      setDraftTitle(normalizedDraft.title);
      setDraftContent(normalizedDraft.content);
      setDraftType(normalizedDraft.draft_type || 'general');

      // Call the optional callback
      if (onSelectDraft) {
        onSelectDraft(normalizedDraft);
      }
    } catch (error) {
      console.error('Failed to load draft:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load draft',
        duration: 3000
      });
    }
  };

  const handleSaveDraft = async () => {
    if (!selectedDraft) return;

    try {
      setIsSaving(true);
      const response = await draftApi.updateDraft(selectedDraft.draft_id, draftTitle, draftContent, undefined, draftType);
      console.log('Save draft response:', response);

      // Normalize the response if needed
      const normalizedTitle = typeof draftTitle === 'string' ? draftTitle : String(draftTitle);
      const normalizedContent = typeof draftContent === 'string' ? draftContent : String(draftContent);

      // Update the draft in the list
      setDrafts(prevDrafts =>
        prevDrafts.map(d =>
          d.draft_id === selectedDraft.draft_id
            ? { ...d, title: normalizedTitle, content: normalizedContent, draft_type: draftType, updated_at: new Date().toISOString() }
            : d
        )
      );

      // Update selected draft
      setSelectedDraft({
        ...selectedDraft,
        title: normalizedTitle,
        content: normalizedContent,
        draft_type: draftType,
        updated_at: new Date().toISOString()
      });

      addNotification({
        type: 'success',
        title: 'Saved',
        message: 'Draft saved successfully',
        duration: 2000
      });
    } catch (error) {
      console.error('Failed to save draft:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to save draft',
        duration: 3000
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportDraft = async () => {
    if (!selectedDraft) return;

    try {
      await exportDraftToDocx({
        title: draftTitle,
        content: draftContent,
        draftType: selectedDraft.draft_type,
        metadata: {
          createdAt: selectedDraft.created_at,
          updatedAt: selectedDraft.updated_at,
        },
      });

      addNotification({
        type: 'success',
        title: 'Exported',
        message: 'Draft exported to DOCX successfully',
        duration: 2000
      });
    } catch (error) {
      console.error('Failed to export draft:', error);
      addNotification({
        type: 'error',
        title: 'Export Failed',
        message: 'Failed to export draft to DOCX',
        duration: 3000
      });
    }
  };

  const getDraftTypeColor = (type: string) => {
    switch (type) {
      case 'claims':
        return 'bg-purple-100 text-purple-800';
      case 'specification':
        return 'bg-blue-100 text-blue-800';
      case 'background':
        return 'bg-amber-100 text-amber-800';
      case 'summary':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Show loading spinner while auth is being checked
  if (authLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show premium blocker if user is not on Premium tier (after auth check completes)
  if (!isPremium) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-gray-50 p-8">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <Lock className="w-16 h-16 mx-auto text-amber-600 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Premium Feature</h2>
            <p className="text-gray-600">
              Drafts is a Premium-only feature. Upgrade to Premium to create, edit, and manage patent draft documents.
            </p>
          </div>
          <div className="space-y-3">
            <div className="text-left bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Premium Features Include:</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Unlimited draft documents</li>
                <li>• Line-numbered professional editor</li>
                <li>• Export to DOCX format</li>
                <li>• Multi-document integration</li>
                <li>• Auto-save and version history</li>
              </ul>
            </div>
            <Button
              onClick={onBack}
              variant="outline"
              className="w-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex bg-gray-50">
      {/* Drafts List - Left Side */}
      <div className="w-96 border-r border-gray-200 bg-white flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
            </div>
            <Button
              onClick={() => setShowNewDraftDialog(true)}
              size="sm"
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              New Draft
            </Button>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileEdit className="w-5 h-5 text-purple-600" />
            My Drafts
          </h2>
        </div>

        {/* Drafts List */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : drafts.length === 0 ? (
              <div className="text-center py-12">
                <FileEdit className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-2">No drafts yet</p>
                <p className="text-sm text-gray-400 mb-4">Create your first draft to get started</p>
                <Button onClick={() => setShowNewDraftDialog(true)} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Draft
                </Button>
              </div>
            ) : (
              drafts.map((draft) => (
                <div
                  key={draft.draft_id}
                  className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                    selectedDraft?.draft_id === draft.draft_id
                      ? 'border-purple-300 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleSelectDraft(draft)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 line-clamp-1 flex-1">
                      {typeof draft.title === 'string' ? draft.title : (draft.title?.title || 'Untitled Draft')}
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDraftToDelete(draft);
                        setShowDeleteDialog(true);
                      }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>

                  <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                    {typeof draft.content === 'string' ? (draft.content || 'No content yet') : (draft.content?.content || 'No content yet')}
                  </p>

                  <div className="flex items-center gap-2 text-xs">
                    <Badge className={getDraftTypeColor(draft.draft_type)}>
                      {draft.draft_type.replace('_', ' ')}
                    </Badge>
                    <span className="text-gray-400 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(draft.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Draft Editor - Right Side */}
      {selectedDraft ? (
        <div className="flex-1 flex flex-col bg-white">
          {/* Editor Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <Input
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                className="text-xl font-semibold border-0 focus-visible:ring-0 px-0"
                placeholder="Draft Title"
              />
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleExportDraft}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export DOCX
                </Button>
                <Button
                  onClick={handleSaveDraft}
                  disabled={isSaving}
                  size="sm"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-3 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">Type:</span>
                <Select value={draftType} onValueChange={setDraftType}>
                  <SelectTrigger className="w-[180px] h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="claims">Claims</SelectItem>
                    <SelectItem value="specification">Specification</SelectItem>
                    <SelectItem value="background">Background</SelectItem>
                    <SelectItem value="summary">Summary</SelectItem>
                    <SelectItem value="detailed_description">Detailed Description</SelectItem>
                    <SelectItem value="abstract">Abstract</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <span>{draftContent.length} characters</span>
              <span>{draftContent.split(/\s+/).filter(w => w.length > 0).length} words</span>
            </div>
          </div>

          {/* Editor Content */}
          <div className="flex-1 overflow-hidden p-6">
            <LineNumberedTextarea
              value={draftContent}
              onChange={setDraftContent}
              placeholder="Start writing your draft here..."
            />
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <FileEdit className="w-20 h-20 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg mb-2">Select a draft to edit</p>
            <p className="text-gray-400 text-sm">or create a new one to get started</p>
          </div>
        </div>
      )}

      {/* New Draft Dialog */}
      <Dialog open={showNewDraftDialog} onOpenChange={setShowNewDraftDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Create New Draft</DialogTitle>
            <DialogDescription>
              Enter a title and select a type for your new draft
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Title</label>
              <Input
                value={newDraftTitle}
                onChange={(e) => setNewDraftTitle(e.target.value)}
                placeholder="Enter draft title"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateDraft();
                  }
                }}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Type</label>
              <Select value={newDraftType} onValueChange={setNewDraftType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select draft type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="specification">Specification</SelectItem>
                  <SelectItem value="claims">Claims</SelectItem>
                  <SelectItem value="background">Background</SelectItem>
                  <SelectItem value="summary">Summary</SelectItem>
                  <SelectItem value="description">Detailed Description</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDraftDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateDraft}>
              Create Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Draft?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{draftToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDraft}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
